import { XStack, View, Text } from "tamagui";
import { colors } from "@styles/colors";
import { isGasSkinV2 } from "@screen/Home/pages/GasShop/skin";

export type OrderUIStatus = "pending" | "waiting_for_pickup" | "delivering" | "completed" | "canceled";

/**
 * 訂單狀態的顏色與文案（單一來源）。
 *
 * 為什麼要抽出來：這張表原本在訂單列表、訂單詳情、帳戶的訂單紀錄各抄了一份，
 * 三邊的底色與文案已經漂開（帳戶那份把「待接單」畫成主色藍、列表是琥珀），
 * 同一筆訂單在兩個頁面看起來會像兩種狀態。
 *
 * 顏色是語意而非裝飾，所以 V1／V2 都用同一組；差別只在外框（見 OrderStatusBadge）。
 */
export const ORDER_STS: Record<OrderUIStatus, { l: string; c: string; bg: string; bar: string }> = {
  pending: { l: "待確認", c: colors.statusPending, bg: colors.bgAmber, bar: colors.statusPending },
  waiting_for_pickup: { l: "待接單", c: colors.statusWaiting, bg: colors.primaryActive, bar: colors.statusWaiting },
  delivering: { l: "配送中", c: colors.statusDelivering, bg: colors.bgPurple, bar: colors.statusDelivering },
  completed: { l: "已完成", c: colors.statusCompleted, bg: colors.bgGreen, bar: colors.statusCompleted },
  canceled: { l: "已取消", c: colors.danger, bg: colors.bgRed, bar: colors.danger },
};

/** 後端狀態字串 → UI 狀態。認不得的一律當「待確認」，不要讓小標消失。 */
export const toOrderUIStatus = (status?: string): OrderUIStatus => {
  switch (status) {
    case "waiting_for_pickup":
      return "waiting_for_pickup";
    case "delivering":
      return "delivering";
    case "completed":
      return "completed";
    case "canceled":
    case "failed":
      return "canceled";
    default:
      return "pending";
  }
};

/**
 * 退款小標。沿用 colors.ts 既有 amber，不新增色票——
 * 不用 danger 紅色（那在這個 app 是「已取消」），退款不是出錯，是金流反向。
 */
export const getRefundBadge = (state?: "none" | "running" | "done") => {
  if (state === "running") return { label: "退款中", bg: colors.bgAmber, fg: "#B45309", dot: colors.amber };
  if (state === "done") return { label: "已退款", bg: "#F5EBDC", fg: colors.amberDark, dot: "#B45309" };
  return null;
};

interface BadgeShape {
  label: string;
  bg: string;
  fg: string;
  dot: string;
}

/** 狀態／退款小標。V2 去掉邊框、只留柔和底色，與卡片的扁平語彙一致；顏色仍帶語意故保留。 */
export const OrderStatusBadge = ({
  status,
  label,
  refundState,
}: {
  /** UI 狀態，或後端回的狀態字串 */
  status?: string;
  /** 後端已給文案時可覆寫（如帳戶訂單紀錄的 status_label） */
  label?: string;
  refundState?: "none" | "running" | "done";
}) => {
  const skinV2 = isGasSkinV2();
  const refund = getRefundBadge(refundState);
  const s = ORDER_STS[toOrderUIStatus(status)];
  // 有退款：只顯示退款中／已退款，不再並排「已完成」等配送狀態
  const b: BadgeShape = refund ?? { label: label || s.l, bg: s.bg, fg: s.c, dot: s.bar };
  return (
    <XStack
      ai="center"
      gap={5}
      bg={b.bg}
      px={skinV2 ? 10 : "$2.5"}
      py={skinV2 ? 4 : "$1"}
      br={20}
      flexShrink={0}
      borderWidth={skinV2 ? 0 : 1}
      borderColor={`${b.dot}55`}>
      <View w={6} h={6} br={3} bg={b.dot} />
      <Text fontSize={12} fow="700" color={b.fg}>
        {b.label}
      </Text>
    </XStack>
  );
};
